import { format, isToday, isTomorrow } from 'date-fns'
import { Check, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { type Reminder, useCompleteReminder, useMyReminders } from '@/models/userReminder'
import { NewReminderDialog } from './NewReminderDialog'
import { ReminderDetailDialog } from './ReminderDetailDialog'

const SKELETON_ROWS = ['a', 'b', 'c', 'd']
const TICK_MS = 30_000 // re-evaluate past/upcoming every 30s so cards drop into Past on time

interface DayGroup {
  key: string
  label: string
  items: Reminder[]
}

function dayLabel(date: Date): string {
  if (isToday(date)) return 'Today'
  if (isTomorrow(date)) return 'Tomorrow'
  return format(date, 'EEEE, MMM d')
}

function groupByDay(reminders: Reminder[]): DayGroup[] {
  const groups: DayGroup[] = []
  for (const reminder of reminders) {
    const date = new Date(reminder.reminderDate)
    const key = format(date, 'yyyy-MM-dd')
    let group = groups.find((g) => g.key === key)
    if (!group) {
      group = { key, label: dayLabel(date), items: [] }
      groups.push(group)
    }
    group.items.push(reminder)
  }
  return groups
}

export function RemindersPage() {
  const { reminders, isLoading } = useMyReminders()
  const completeMutation = useCompleteReminder()
  const [selected, setSelected] = useState<Reminder | null>(null)
  const [open, setOpen] = useState(false)
  const [newOpen, setNewOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [])

  const active = reminders.filter((r) => !r.completedAt)
  const completed = reminders
    .filter((r) => r.completedAt)
    .sort((a, b) => (b.completedAt ?? '').localeCompare(a.completedAt ?? ''))
  const upcoming = active.filter((r) => new Date(r.reminderDate).getTime() >= now)
  const past = active
    .filter((r) => new Date(r.reminderDate).getTime() < now)
    .sort((a, b) => new Date(b.reminderDate).getTime() - new Date(a.reminderDate).getTime())

  const upcomingGroups = groupByDay(upcoming)

  const openReminder = (reminder: Reminder) => {
    setSelected(reminder)
    setOpen(true)
  }

  const renderRow = (reminder: Reminder, timeLabel: string, completable: boolean) => (
    <div
      key={reminder.id}
      className="bg-surface flex items-center gap-1 rounded-md border border-neutral-200"
    >
      <Button
        variant="ghost"
        onClick={() => openReminder(reminder)}
        className="h-auto min-w-0 flex-1 justify-between px-3 py-2 text-left font-normal"
      >
        <span className="text-md truncate">{reminder.message}</span>
        <span className="text-sm text-neutral-500">{timeLabel}</span>
      </Button>
      {completable && (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Complete reminder"
          className="mr-1 text-neutral-400 hover:text-green-600"
          disabled={completeMutation.isPending}
          onClick={() => completeMutation.mutate(reminder.id)}
        >
          <Check className="size-4" />
        </Button>
      )}
    </div>
  )

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">My reminders</h1>
        <Button onClick={() => setNewOpen(true)} className="text-md">
          <Plus className="mr-2 size-4" />
          New reminder
        </Button>
      </div>
      {isLoading && (
        <div className="flex flex-col gap-2">
          {SKELETON_ROWS.map((key) => (
            <Skeleton key={key} className="h-11 w-full rounded-md" />
          ))}
        </div>
      )}
      {!isLoading && reminders.length === 0 && <p className="text-neutral-500">No reminders.</p>}
      <div className="flex flex-col gap-6">
        {upcomingGroups.map((group) => (
          <div key={group.key}>
            <h2 className="mb-2 text-sm font-semibold text-neutral-500">{group.label}</h2>
            <div className="flex flex-col gap-2">
              {group.items.map((reminder) =>
                renderRow(reminder, format(new Date(reminder.reminderDate), 'h:mm a'), true)
              )}
            </div>
          </div>
        ))}
        {past.length > 0 && (
          <div>
            <h2 className="mb-2 text-sm font-semibold text-neutral-400">Past</h2>
            <div className="flex flex-col gap-2 opacity-70">
              {past.map((reminder) =>
                renderRow(reminder, format(new Date(reminder.reminderDate), 'MMM d, h:mm a'), true)
              )}
            </div>
          </div>
        )}
        {completed.length > 0 && (
          <div>
            <h2 className="mb-2 text-sm font-semibold text-neutral-400">Completed</h2>
            <div className="flex flex-col gap-2 opacity-60">
              {completed.map((reminder) =>
                renderRow(
                  reminder,
                  reminder.completedAt ? format(new Date(reminder.completedAt), 'MMM d, h:mm a') : '',
                  false
                )
              )}
            </div>
          </div>
        )}
      </div>
      <ReminderDetailDialog reminder={selected} open={open} onOpenChange={setOpen} />
      <NewReminderDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  )
}
